import { useInfiniteBusinessSectorsQuery } from "@ac-expo/api";
import { toggleArrayItem } from "@ac-expo/utils";
import { CheckIcon } from "@phosphor-icons/react";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import { Button } from "../ui/Button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/Command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/Popover";

type BusinessSectorMultiSelectProps = {
  children: ReactNode;
  selectedIds: string[];
  onConfirm: (selectedIds: string[]) => void;
  eventId?: string;
  confirmLabel?: string;
  resetLabel?: string;
};

export const BusinessSectorMultiSelect = ({
  children,
  selectedIds,
  onConfirm,
  eventId,
  confirmLabel = "Confirmer",
  resetLabel = "Réinitialiser",
}: BusinessSectorMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedIds);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalSelectedIds(selectedIds);
      setSearchText("");
    }
  }, [isOpen, selectedIds]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteBusinessSectorsQuery({
      searchText: debouncedSearchText || undefined,
      eventId,
      excludeChildren: true,
    });

  const businessSectors = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

  const handleToggle = useCallback((id: string) => {
    setLocalSelectedIds((prev) => toggleArrayItem(prev, id));
  }, []);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onConfirm(localSelectedIds);
    }
    setIsOpen(open);
  };

  const handleConfirm = () => {
    handleOpenChange(false);
  };

  const handleReset = () => {
    setLocalSelectedIds([]);
  };

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;
    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Rechercher un secteur..."
            value={searchText}
            onValueChange={setSearchText}
          />
          <CommandList
            ref={listRef}
            onScroll={handleScroll}
            onWheel={handleWheel}
            className="max-h-[250px]"
          >
            {isLoading ? (
              <div className="py-6 text-center text-sm text-gray-500">Chargement...</div>
            ) : (
              <>
                <CommandEmpty>Aucun secteur trouvé</CommandEmpty>
                <CommandGroup>
                  {businessSectors.map((sector) => {
                    const isSelected = localSelectedIds.includes(sector.id);
                    return (
                      <CommandItem
                        key={sector.id}
                        value={sector.id}
                        onSelect={() => handleToggle(sector.id)}
                        className="flex cursor-pointer items-center gap-2"
                      >
                        <div
                          className={`flex size-4 items-center justify-center rounded border ${
                            isSelected ? "border-primary-500 bg-primary-500" : "border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <CheckIcon size={12} className="text-white" weight="bold" />
                          )}
                        </div>
                        <span className="flex-1 text-sm">{sector.name}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                {isFetchingNextPage && (
                  <div className="py-2 text-center text-sm text-gray-500">Chargement...</div>
                )}
              </>
            )}
          </CommandList>
          <div className="flex gap-2 border-t p-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="flex-1"
            >
              {resetLabel}
            </Button>
            <Button
              type="button"
              variant="blue"
              size="sm"
              onClick={handleConfirm}
              className="flex-1"
            >
              {confirmLabel}
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
